import React from 'react';
import { Box, HStack, Text, Badge, VStack } from '@chakra-ui/react';
import { Project } from '../types';
import {
  formatProjectDate,
  getProjectCardTheme,
  getProjectStatusBadges,
  PROJECT_CARD_THEME,
  ProjectCardTheme,
} from '../utils/projectCardTheme';
import { hasProjectUpdatesSinceVisit } from '../utils/activityIndicators';
import { ActivityIndicatorBadge } from './ActivityIndicatorBadge';

interface ProjectDashboardListProps {
  projects: Project[];
  userId?: string;
  onProjectClick: (projectId: string) => void;
}

interface ProjectRowProps {
  project: Project;
  userId?: string;
  isLast: boolean;
  onClick: () => void;
}

function ProjectProgress({ project, themeKey }: { project: Project; themeKey: ProjectCardTheme }) {
  const palette = PROJECT_CARD_THEME[themeKey];
  const pct = Math.min(100, Math.max(0, project.taskCompletion?.percent ?? 0));
  return (
    <Box minW={{ md: '120px' }}>
      <HStack justifyContent="space-between" mb={1}>
        <Text fontSize="xs" color="gray.500">Completed</Text>
        <Text fontSize="xs" fontWeight="semibold" color="gray.700">{pct}%</Text>
      </HStack>
      <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          w={`${pct}%`}
          bgGradient="to-r"
          gradientFrom={palette.progressFrom}
          gradientTo={palette.progressTo}
          borderRadius="full"
        />
      </Box>
      <Text fontSize="xs" color="gray.400" mt={1}>
        {project.taskCompletion?.completed ?? 0} / {project.taskCompletion?.total ?? 0} tasks
      </Text>
    </Box>
  );
}

const ProjectListRowDesktop: React.FC<ProjectRowProps> = ({ project, userId, isLast, onClick }) => {
  const themeKey = getProjectCardTheme(project);
  const badges = getProjectStatusBadges(themeKey, project);
  const hasUpdates = hasProjectUpdatesSinceVisit(project, userId);

  return (
    <HStack
      px={4}
      py={4}
      gap={4}
      alignItems="flex-start"
      borderBottomWidth={isLast ? 0 : 1}
      borderColor="gray.100"
      bg={PROJECT_CARD_THEME[themeKey].bg}
      borderLeftWidth={hasUpdates ? 3 : 0}
      borderLeftColor={hasUpdates ? 'blue.300' : 'transparent'}
      _hover={{ bg: 'blue.50', cursor: 'pointer' }}
      onClick={onClick}
      display={{ base: 'none', lg: 'flex' }}
    >
      <Box flex={2} minW={0}>
        <Text fontWeight="semibold" color="gray.800" mb={1}>{project.name}</Text>
        <HStack gap={1} flexWrap="wrap">
          {hasUpdates && <ActivityIndicatorBadge label="New activity" variant="project" />}
          {badges.map((b) => (
            <Badge key={b.label} size="sm" colorScheme={b.colorScheme} borderRadius="full" px={2}>
              {b.label}
            </Badge>
          ))}
        </HStack>
      </Box>
      <Box flex={2} minW={0}>
        <Text fontSize="sm" color="gray.600" lineClamp={2}>
          {project.description || 'No description'}
        </Text>
      </Box>
      <Box flex={1} minW="100px">
        <Text fontSize="sm" color="gray.700">{formatProjectDate(project.startDate)}</Text>
      </Box>
      <Box flex={1} minW="100px">
        <Text
          fontSize="sm"
          fontWeight={themeKey === 'critical' || themeKey === 'warning' ? 'semibold' : 'medium'}
          color={themeKey === 'critical' ? 'red.700' : themeKey === 'warning' ? 'orange.800' : 'gray.700'}
        >
          {formatProjectDate(project.endDate)}
        </Text>
      </Box>
      <Box flex={2} minW="140px">
        <ProjectProgress project={project} themeKey={themeKey} />
      </Box>
    </HStack>
  );
};

const ProjectListRowMobile: React.FC<ProjectRowProps> = ({ project, userId, isLast, onClick }) => {
  const themeKey = getProjectCardTheme(project);
  const palette = PROJECT_CARD_THEME[themeKey];
  const badges = getProjectStatusBadges(themeKey, project);
  const hasUpdates = hasProjectUpdatesSinceVisit(project, userId);

  return (
    <Box
      px={4}
      py={4}
      borderBottomWidth={isLast ? 0 : 1}
      borderColor="gray.100"
      bg={palette.bg}
      borderLeftWidth={hasUpdates ? 3 : 3}
      borderLeftColor={hasUpdates ? 'blue.300' : palette.border}
      _hover={{ bg: 'blue.50', cursor: 'pointer' }}
      onClick={onClick}
      display={{ base: 'block', lg: 'none' }}
    >
      <HStack justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
        <Text fontWeight="semibold" color="gray.800" flex={1}>{project.name}</Text>
      </HStack>
      <HStack gap={1} flexWrap="wrap" mb={2}>
        {hasUpdates && <ActivityIndicatorBadge label="New activity" variant="project" />}
        {badges.map((b) => (
          <Badge key={b.label} size="sm" colorScheme={b.colorScheme} borderRadius="full" px={2}>
            {b.label}
          </Badge>
        ))}
      </HStack>
      <Text fontSize="sm" color="gray.600" mb={3}>
        {project.description || 'No description'}
      </Text>
      <VStack align="stretch" gap={2} mb={3}>
        <HStack gap={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="medium" minW="70px">Start</Text>
          <Text fontSize="sm" color="gray.700">{formatProjectDate(project.startDate)}</Text>
        </HStack>
        <HStack gap={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="medium" minW="70px">End</Text>
          <Text
            fontSize="sm"
            fontWeight={themeKey === 'critical' || themeKey === 'warning' ? 'semibold' : 'medium'}
            color={themeKey === 'critical' ? 'red.700' : themeKey === 'warning' ? 'orange.800' : 'gray.700'}
          >
            {formatProjectDate(project.endDate)}
          </Text>
        </HStack>
      </VStack>
      <ProjectProgress project={project} themeKey={themeKey} />
    </Box>
  );
};

const ProjectDashboardList: React.FC<ProjectDashboardListProps> = ({ projects, userId, onProjectClick }) => (
  <Box bg="white" borderRadius="xl" borderWidth={1} borderColor="gray.200" overflow="hidden">
    <HStack
      px={4}
      py={3}
      bg="gray.50"
      borderBottomWidth={1}
      borderColor="gray.200"
      fontSize="xs"
      fontWeight="semibold"
      color="gray.600"
      textTransform="uppercase"
      letterSpacing="wider"
      gap={4}
      display={{ base: 'none', lg: 'flex' }}
    >
      <Box flex={2}>Project</Box>
      <Box flex={2}>Description</Box>
      <Box flex={1} minW="100px">Start</Box>
      <Box flex={1} minW="100px">End</Box>
      <Box flex={2} minW="140px">Progress</Box>
    </HStack>
    {projects.map((project, index) => (
      <React.Fragment key={project._id}>
        <ProjectListRowDesktop
          project={project}
          userId={userId}
          isLast={index === projects.length - 1}
          onClick={() => onProjectClick(project._id)}
        />
        <ProjectListRowMobile
          project={project}
          userId={userId}
          isLast={index === projects.length - 1}
          onClick={() => onProjectClick(project._id)}
        />
      </React.Fragment>
    ))}
  </Box>
);

export default ProjectDashboardList;
